import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";

import { ConfigProvider } from "./context/ConfigContext";
import { CartProvider } from "./context/CartContext";
import { I18nProvider } from "./context/I18nContext";
import { AuthProvider } from "./context/AuthContext";
import { ShopAuthProvider } from "./context/ShopAuthContext";
import { GlobalLoadingProvider } from "./context/GlobalLoadingProvider";
import { BrandingProvider } from "./context/BrandingContext";
import Layout from "./components/Layout";

import HomePage from "./pages/HomePage";
import ShippingToCuracaoPage from "./pages/ShippingToCuracaoPage";
import ShippingToArubaPage from "./pages/ShippingToArubaPage";
import ShippingToBonairePage from "./pages/ShippingToBonairePage";
import ShippingToSintMaartenPage from "./pages/ShippingToSintMaartenPage";
import ShippingToSurinamePage from "./pages/ShippingToSurinamePage";
import CmsPageRenderer from "./components/CmsPageRenderer";
import { SiteSchema } from "./components/Schema";
import OnlinePaymentPageBanner from "./components/OnlinePaymentPageBanner";
import WordAgentPageBanner from "./components/WordAgentPageBanner";
import DocumentenPageBanner from "./components/DocumentenPageBanner";
import BtwPageBanner from "./components/BtwPageBanner";
import ContactPageBanner from "./components/ContactPageBanner";
import PageBreadcrumbs from "./components/PageBreadcrumbs";
import RelatedDestinations from "./components/RelatedDestinations";
import ShippingInfoModal, { ShippingInfoButton } from "./components/ShippingInfoModal";
import PaklijstPage from "./pages/PaklijstPage";
import BoekingsformulierPage from "./pages/BoekingsformulierPage";
import PublicQuoteSharePage from "./pages/PublicQuoteSharePage";
import VolumemeterPage from "./pages/VolumemeterPage";
import CbmCalculatorPage from "./pages/CbmCalculatorPage";
import FactuurMakenPage from "./pages/FactuurMakenPage";
import AirFreightPage from "./pages/AirFreightPage";
import SeaFreightPage from "./pages/SeaFreightPage";
import MovingPage from "./pages/MovingPage";
import PickupDeliveryPage from "./pages/PickupDeliveryPage";
import PickupCalculatorPage from "./pages/PickupCalculatorPage";
import TrackingPage from "./pages/TrackingPage";
import ZakelijkPage from "./pages/ZakelijkPage";
import FAQPage from "./pages/FAQPage";
import ShopDropShipPage from "./pages/ShopDropShipPage";
import WebshopPage from "./pages/WebshopPage";
import WebshopSuccessPage from "./pages/WebshopSuccessPage";
import WebshopCancelPage from "./pages/WebshopCancelPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import HubsPage from "./pages/HubsPage";
import BlogListPage from "./pages/BlogListPage";
import BlogPostPage from "./pages/BlogPostPage";
import My5999CargoPage from "./pages/My5999CargoPage";
import { PortalLayout } from "./components/customer/PortalLayout";
import CustomerPortal, { PortalShipments, PortalShipmentDetail, PortalAllDocuments } from "./pages/CustomerPortal";
import PortalMessages, { PortalMessageDetail } from "./pages/PortalMessages";
import PortalProfile, { PortalAddressBook } from "./pages/PortalProfile";
import CustomerActivation from "./pages/CustomerActivation";
import PortalOrders from "./pages/PortalOrders";
import PreAlerts, { PreAlertDetail } from "./pages/PreAlerts";
import PreAlertForm from "./pages/PreAlertForm";
import NotFoundPage from "./pages/NotFoundPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminForgotPasswordPage from "./pages/AdminForgotPasswordPage";
import AdminResetPasswordPage from "./pages/AdminResetPasswordPage";
import AdminPage from "./pages/AdminPage";
import {
  LoginPage as ShopLoginPage,
  DashboardPage as ShopDashboardPage,
  OrderDetailPage as ShopOrderDetailPage,
  ProfilePage as ShopProfilePage,
  VerifyEmailPage as ShopVerifyEmailPage,
  ForgotPasswordPage as ShopForgotPasswordPage,
  ResetPasswordPage as ShopResetPasswordPage,
} from "./pages/ShopAccountPages";
import CustomerOnboarding from "./pages/CustomerOnboarding";
import AgentAccessPage from "./pages/AgentAccessPage";
import AgentLoginPage from "./pages/AgentLoginPage";
import AgentInvitationPage from "./pages/AgentInvitationPage";
import AgentApplicationForm from "./components/AgentApplicationForm";
import AgentCommunicationsPage from "./pages/AgentCommunicationsPage";
import { Toaster } from "./components/ui/sonner";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
    } else {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [pathname, hash]);
  return null;
}

function PublicRoutes() {
  const requiredAgentForm = {type:"agent_application_form", element:<section className="max-w-5xl mx-auto px-6 py-12" data-testid="word-agent-required-form"><h2 className="text-base md:text-lg font-chivo font-bold mb-6">Agentaanvraag</h2><AgentApplicationForm /></section>};
  return (
    <Layout>
      {/* SEO Q1+Q2 — site-wide schema markup: Organization, WebSite, LocalBusiness */}
      <SiteSchema />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/shipping-to-curacao" element={<ShippingToCuracaoPage />} />
        <Route path="/shipping-to-aruba" element={<ShippingToArubaPage />} />
        <Route path="/shipping-to-bonaire" element={<ShippingToBonairePage />} />
        <Route path="/shipping-to-sint-maarten" element={<ShippingToSintMaartenPage />} />
        <Route path="/shipping-to-suriname" element={<ShippingToSurinamePage />} />
        <Route
          path="/word-agent"
          element={
            <CmsPageRenderer
              slug="word-agent"
              testid="cms-word-agent"
              requiredBlock={requiredAgentForm}
              hideBlockTypes={["hero"]}
              topSection={<WordAgentPageBanner />}
            />
          }
        />
        <Route
          path="/word-ook-agent"
          element={
            <CmsPageRenderer
              slug="word-agent"
              testid="cms-word-agent"
              requiredBlock={requiredAgentForm}
              hideBlockTypes={["hero"]}
              topSection={<WordAgentPageBanner />}
            />
          }
        />
        <Route
          path="/agent-worden"
          element={
            <CmsPageRenderer
              slug="word-agent"
              testid="cms-word-agent"
              requiredBlock={requiredAgentForm}
              hideBlockTypes={["hero"]}
              topSection={<WordAgentPageBanner />}
            />
          }
        />
        <Route
          path="/online-betalen"
          element={
            <CmsPageRenderer
              slug="online-betalen"
              testid="cms-online-betalen"
              hideBlockTypes={["hero"]}
              topSection={<OnlinePaymentPageBanner />}
            />
          }
        />
        <Route path="/documenten" element={
          <>
            <ShippingInfoModal autoOpen />
            <CmsPageRenderer slug="documenten" testid="cms-documenten"
              hideBlockTypes={["hero"]}
              topSection={<DocumentenPageBanner />}
              breadcrumbs={<PageBreadcrumbs items={[
                { name: "Home", url: "/" },
                { name: "Klantenservice", url: "/contact" },
                { name: "Documenten" },
              ]} testid="documenten-breadcrumbs" />}
              extraSections={
                <>
                  <div className="bg-amber-50 border-y border-amber-200 py-6">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between flex-wrap gap-3">
                      <div className="text-sm text-amber-900">Onvolledige documentatie betekent geen verzending. Lees onze samenvatting voor nieuwe/gebruikte goederen en voertuigen.</div>
                      <ShippingInfoButton testId="documenten-shipping-info-trigger" />
                    </div>
                  </div>
                  <RelatedDestinations
                    testid="documenten-related"
                    eyebrow="Documenten per bestemming"
                    title="Documenten voor verzending naar de Caribbean"
                    description="Per bestemming kunnen documentatie en lokale eisen verschillen. Bekijk wat wij naar elke bestemming verzorgen."
                    anchorTemplate="Documenten voor zending naar {name}"
                  />
                </>
              }
            />
          </>
        } />
        <Route path="/btw-terugvragen" element={
          <CmsPageRenderer
            slug="btw-terugvragen"
            testid="cms-btw-terugvragen"
            hideBlockTypes={["hero"]}
            topSection={<BtwPageBanner />}
            breadcrumbs={<PageBreadcrumbs items={[
              { name: "Home", url: "/" },
              { name: "Klantenservice", url: "/contact" },
              { name: "BTW terugvragen" },
            ]} testid="btw-breadcrumbs" />}
          />
        } />
        <Route path="/paklijst" element={<PaklijstPage />} />
        <Route path="/boekingsformulier" element={<BoekingsformulierPage />} />
        <Route path="/offerte/:token" element={<PublicQuoteSharePage />} />
        <Route path="/volumemeter" element={<VolumemeterPage />} />
        <Route path="/cbm-calculator" element={<CbmCalculatorPage />} />
        <Route path="/factuur-maken" element={<FactuurMakenPage />} />
        <Route path="/invoice-generator" element={<FactuurMakenPage />} />
        <Route path="/air-freight" element={<AirFreightPage />} />
        <Route path="/luchtvracht" element={<AirFreightPage />} />
        <Route path="/sea-freight" element={<SeaFreightPage />} />
        <Route path="/zeevracht" element={<SeaFreightPage />} />
        <Route path="/moving" element={<MovingPage />} />
        <Route path="/verhuizing" element={<MovingPage />} />
        <Route path="/pickup-delivery" element={<PickupDeliveryPage />} />
        <Route path="/pickup-calculator" element={<PickupCalculatorPage />} />
        <Route path="/tracking" element={<TrackingPage />} />
        <Route path="/zakelijk" element={<ZakelijkPage />} />
        <Route path="/zakelijke-zendingen" element={<ZakelijkPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/shop-drop-ship" element={<ShopDropShipPage />} />
        <Route path="/webshop" element={<WebshopPage />} />
        <Route path="/webshop/success" element={<WebshopSuccessPage />} />
        <Route path="/webshop/cancel" element={<WebshopCancelPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/hubs" element={<HubsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/my5999cargo" element={<My5999CargoPage />} />
        <Route path="/my5999" element={<My5999CargoPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Layout>
  );
}

function AppShell() {
  return (
    <Routes>
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
      <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
      <Route path="/admin" element={<AdminPage />} />
      {/* Phase B — Shop customer account (apart van admin auth) */}
      <Route path="/shop-account/register" element={<CustomerOnboarding />} />
      <Route path="/account/onboarding" element={<CustomerOnboarding />} />
      <Route path="/agent/login" element={<AgentLoginPage />} />
      <Route path="/agent/uitnodiging" element={<AgentInvitationPage />} />
      <Route path="/agent/login/:agentPublicId" element={<AgentLoginPage />} />
      <Route path="/agent/*" element={<AgentAccessPage />} />
      <Route path="/agent/communicatie" element={<AgentCommunicationsPage />} />
      <Route path="/shop-account/login" element={<ShopLoginPage />} />
      <Route path="/shop-account/activate" element={<CustomerActivation />} />
      <Route path="/shop-account/verify" element={<ShopVerifyEmailPage />} />
      <Route path="/shop-account/forgot-password" element={<ShopForgotPasswordPage />} />
      <Route path="/shop-account/reset-password" element={<ShopResetPasswordPage />} />
      <Route path="/shop-account/orders/:id" element={<ShopOrderDetailPage />} />
      <Route path="/shop-account/profile" element={<Navigate to="/mijn-5999cargo/gegevens" replace />} />
      <Route path="/shop-account" element={<Navigate to="/mijn-5999cargo" replace />} />
      <Route path="/mijn-5999cargo" element={<PortalLayout />}>
        <Route index element={<CustomerPortal />} />
        <Route path="zendingen" element={<PortalShipments />} />
        <Route path="zendingen/:id" element={<PortalShipmentDetail />} />
        <Route path="pre-alerts" element={<PreAlerts />} />
        <Route path="pre-alerts/nieuw" element={<PreAlertForm />} />
        <Route path="pre-alerts/:id" element={<PreAlertDetail />} />
        <Route path="pre-alerts/:id/bewerken" element={<PreAlertForm />} />
        <Route path="documenten" element={<PortalAllDocuments />} />
        <Route path="berichten" element={<PortalMessages />} />
        <Route path="berichten/:id" element={<PortalMessageDetail />} />
        <Route path="bestellingen" element={<PortalOrders />} />
        <Route path="gegevens" element={<PortalProfile />} />
        <Route path="profiel" element={<Navigate to="/mijn-5999cargo/gegevens" replace />} />
        <Route path="adresboek" element={<PortalAddressBook />} />
      </Route>
      <Route path="*" element={<PublicRoutes />} />
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
      <ConfigProvider>
        <AuthProvider>
          <ShopAuthProvider>
            <BrandingProvider>
              <CartProvider>
                <GlobalLoadingProvider>
                  <BrowserRouter>
                    <ScrollToTop />
                    <AppShell />
                    <Toaster richColors position="top-right" />
                  </BrowserRouter>
                </GlobalLoadingProvider>
              </CartProvider>
            </BrandingProvider>
          </ShopAuthProvider>
        </AuthProvider>
      </ConfigProvider>
    </I18nProvider>
  );
}

export default App;
